import { GameFullGame, PlateAppearance } from "../types/gameFull";

/**
 * The game service mints plate appearance ids deterministically
 * (win-game-service src/domain/plateAppearanceId.ts):
 * `<gameId>-i<inning>-<top|bot>-pa<ordinal>`, where the ordinal is how many
 * plate appearances its half had before it, from 0, across every visit to the
 * half. Plate appearances opened ahead of their first pitch carry no
 * startedAt, so the ordinal is what orders them.
 */
const PLATE_APPEARANCE_ID = /-i(\d+)-(top|bot)-pa(\d+)$/;

export type PlateAppearanceIdParts = {
  inning: number;
  isTopOfInning: boolean;
  ordinal: number;
};

/** The id's half-inning and ordinal, or null for an id not in that format. */
export const parsePlateAppearanceId = (
  plateAppearanceId: string,
): PlateAppearanceIdParts | null => {
  const match = plateAppearanceId.match(PLATE_APPEARANCE_ID);
  if (!match) return null;
  const [, inning, half, ordinal] = match;
  return {
    inning: Number(inning),
    isTopOfInning: half === "top",
    ordinal: Number(ordinal),
  };
};

/**
 * The plate appearance's 1-based place in its half-inning, as omnibus's
 * paOfInning counts it (it restarts at 1 each half). Null when the id doesn't
 * say.
 */
export const getPaOfInningFromId = (plateAppearanceId: string) => {
  const parts = parsePlateAppearanceId(plateAppearanceId);
  return parts ? parts.ordinal + 1 : null;
};

/**
 * paOfInning for a tag with no pitch (a state or player tag): the open plate
 * appearance's. With none open, the next one's: the half's count, which then
 * doesn't include it yet.
 */
export const getLivePaOfInning = (game: GameFullGame) =>
  (game.currentPlateAppearanceId &&
    getPaOfInningFromId(game.currentPlateAppearanceId)) ||
  (game.plateAppearancesInHalfInning ?? 0) + 1;

/**
 * Orders plate appearances oldest first: by half-inning, then by ordinal.
 * Ids not in the service's format fall back to startedAt.
 */
export const comparePlateAppearances = (
  a: PlateAppearance,
  b: PlateAppearance,
) => {
  const aParts = parsePlateAppearanceId(a.plateAppearanceId);
  const bParts = parsePlateAppearanceId(b.plateAppearanceId);
  if (!aParts || !bParts) return a.startedAt.localeCompare(b.startedAt);
  return (
    aParts.inning - bParts.inning ||
    Number(bParts.isTopOfInning) - Number(aParts.isTopOfInning) ||
    aParts.ordinal - bParts.ordinal
  );
};
